import { Link } from 'react-router-dom'
import { ArrowRight, Sparkles, Code2, Zap, Shield, Users } from 'lucide-react'
import Button from '@/components/Button'

const Home = () => {
  const features = [
    {
      icon: Code2,
      title: 'Code Generation',
      description: 'Generate complete functions, components, and modules from natural language descriptions'
    },
    {
      icon: Zap,
      title: 'Rapid Development',
      description: 'Build projects faster with AI-powered scaffolding and iterative development'
    },
    {
      icon: Shield,
      title: 'Quality Assurance',
      description: 'Built-in best practices, testing, and security considerations'
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Scalable workflows and patterns for multi-developer projects'
    }
  ]

  const stats = [
    { number: '15', label: 'Comprehensive Sections', description: 'Covering every aspect of AI development' },
    { number: '50+', label: 'Practical Examples', description: 'Real-world use cases and code samples' },
    { number: '5min', label: 'Quick Start', description: 'Get up and running in minutes' },
    { number: '100%', label: 'Open Source', description: 'Free guide for the community' }
  ]

  return (
    <div id="main-content" className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gray-50 dark:bg-gray-900 py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5 dark:opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="flex items-center space-x-2 bg-primary-200 dark:bg-primary-800 px-4 py-2 rounded-full border border-primary-300 dark:border-primary-600">
                <Sparkles className="w-5 h-5 text-primary-700 dark:text-primary-200" />
                <span className="text-sm font-medium text-primary-800 dark:text-primary-200">
                  AI-Powered Development
                </span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6 animate-fade-in">
              Master{' '}
              <span className="text-blue-600 dark:text-blue-400">
                GitHub Copilot Agent
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-800 dark:text-gray-300 max-w-4xl mx-auto mb-10 animate-slide-up">
              The complete jumpstart guide for AI-powered development in VS Code. 
              Learn proven patterns, workflows, and best practices to 10x your productivity.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-slide-up">
              <Link to="/guide">
                <Button size="xl" variant="primary" className="group shadow-lg hover:shadow-xl">
                  <span>Read Complete Guide</span>
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              
              <Link to="/quick-start">
                <Button size="xl" variant="outline" className="shadow-md hover:shadow-lg">
                  Quick Start
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Why This Guide?
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              This comprehensive guide provides everything you need to master AI-powered development, 
              from basic setup to advanced automation patterns.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="group p-6 bg-white dark:bg-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-500 shadow-sm"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 bg-primary-200 dark:bg-primary-700 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary-300 dark:group-hover:bg-primary-600 transition-colors shadow-sm">
                  <feature.icon className="w-6 h-6 text-primary-800 dark:text-primary-200" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-700 dark:text-gray-200">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                className="text-center"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="text-4xl md:text-5xl font-bold mb-2 animate-bounce-gentle text-white">
                  {stat.number}
                </div>
                <div className="text-lg md:text-xl font-semibold mb-1 text-slate-200">
                  {stat.label}
                </div>
                <div className="text-sm text-slate-300">
                  {stat.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
            Ready to Transform Your Development Workflow?
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
            Join thousands of developers who have accelerated their productivity with AI-powered development.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/guide">
              <Button size="lg" variant="primary" className="w-full sm:w-auto shadow-lg hover:shadow-xl">
                Start Learning
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            
            <Link to="/examples">
              <Button size="lg" variant="outline" className="w-full sm:w-auto">
                View Examples
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
